import type { Directive } from 'vue'

/**
 * Force-syncs a radio/checkbox's DOM state with the bound value on every patch.
 *
 * A plain `:checked="expr"` binding is not enough. Vue only writes a DOM prop
 * when the *vdom* value changed:
 *
 *     if (next !== prev || key === "value") hostPatchProp(el, key, prev, next, ...)
 *
 * but a user click mutates `el.checked` directly, behind Vue's back. Whenever a
 * click resolves back to the same bound value (`false → false` — routine for
 * radios, where picking a row can leave the row unselected in app state), Vue
 * skips the write and the browser's stale check stays on screen indefinitely.
 * Native radio grouping does not clean it up either once two inputs sit in
 * different `name` groups.
 *
 * Directive `updated` hooks are not diffed the way props are — they run on every
 * patch of the element — so this always reconciles the DOM back to app state.
 * Use it *instead of* `:checked` on any input whose checked state is derived
 * from shared selection state.
 */
export const vSyncChecked: Directive<HTMLInputElement, boolean> = {
  mounted(el, binding) {
    el.checked = binding.value
  },
  updated(el, binding) {
    if (el.checked !== binding.value) el.checked = binding.value
  },
}
